import * as ActionTypes from './actionTypes'
import generateKey from 'uuid'
import MediasAPI from 'remote/medias'
import FilesAPI from 'remote/files'
import acceptFile from 'attr-accept'
import { apiCapableActionCreator } from '../apiActionHelpers'
import { actions as UIActions, selectors as UISelectors } from 'data/ui'
import { selectors as PaymentSelectors } from 'data/payment'
import { FILE_LIMIT_EXCEEDED } from 'components/Utils/Modal/constants'
import { selectors as OrganizationResourcesUsageSelectors } from 'data/organizationResourcesUsage'
import {
  FREE_MAX_FILE_SIZE,
  MAX_FILE_SIZE,
  MAX_IMAGE_SIZE,
  IMAGE_ACCEPTED_TYPES,
  FILE_ACCEPTED_TYPES,
} from './record'
import { formatFileSize } from './utils'

export const addMediaFileToNote = apiCapableActionCreator(
  ({ uuid = generateKey(), file, meta }) => ({ apiConfig }) => ({
    getState,
    graphQLClient,
  }) => {
    if (!file) {
      return false
    }

    if (PaymentSelectors.isLimited(getState())) {
      if (file.size > FREE_MAX_FILE_SIZE) {
        return UIActions.setActiveModal({
          id: FILE_LIMIT_EXCEEDED,
          meta: {
            title: 'Upload error, file too large',
            subtitle:
              '...for now! Upgrade plans for your team to upload files over 10Mb.',
            illustration: false,
          },
        })
      }
      const storage = OrganizationResourcesUsageSelectors.getOrganizationResourcesUsage(
        getState()
      ).toJS().storage
      if (file.size + storage.current > storage.limit) {
        return UIActions.setActiveModal({
          id: FILE_LIMIT_EXCEEDED,
          meta: {
            title: 'You need more space!',
            subtitle:
              "You've exceeded the free plan's 2GB storage limit. Upgrade to give your team more storage!",
            illustration: true,
          },
        })
      }
    }

    if (acceptFile(file, IMAGE_ACCEPTED_TYPES) && file.size > MAX_IMAGE_SIZE) {
      return UIActions.createErrorToasterMessage({
        title: `Only images lower than ${formatFileSize(
          MAX_IMAGE_SIZE
        )} are supported.`,
      })
    }

    if (!acceptFile(file, FILE_ACCEPTED_TYPES)) {
      return UIActions.createErrorToasterMessage({
        title: `File type not supported.`,
      })
    }

    if (file.size > MAX_FILE_SIZE) {
      return UIActions.createErrorToasterMessage({
        title: `Only files lower than ${formatFileSize(
          MAX_FILE_SIZE
        )} are supported.`,
      })
    }

    const noteId = UISelectors.getActiveNoteId(getState())
    return {
      type: ActionTypes.ADD_MEDIA_FILE_TO_NOTE,
      payload: FilesAPI.uploadFile(apiConfig, {
        file,
      }).then(url =>
        graphQLClient
          .mutate({
            query: MediasAPI.createNoteMedia,
            variables: {
              media: {
                noteId,
                url,
                name: file.name,
                meta: {
                  size: file.size,
                },
              },
            },
          })
          .then(({ data }) => data.createNoteMedia)
      ),
      meta: {
        uuid,
        meta: {
          name: file.name,
          size: file.size,
          ...meta,
        },
      },
    }
  }
)

export const uploadFile = ({ uuid, file, meta }) => {
  if (!acceptFile(file, FILE_ACCEPTED_TYPES)) {
    return UIActions.createErrorToasterMessage({
      title: `File type not supported.`,
    })
  }

  if (file.size > MAX_FILE_SIZE) {
    return UIActions.createErrorToasterMessage({
      title: `Only files lower than ${formatFileSize(
        MAX_FILE_SIZE
      )} are supported.`,
    })
  }
  return ({ getState }) => ({
    type: ActionTypes.UPLOAD_FILE,
    payload: FilesAPI.uploadFile(getState().config, { file }),
    meta: {
      uuid,
      meta,
    },
  })
}

export const uploadImage = ({ uuid, file, meta }) => {
  if (!file) {
    return false
  }

  if (!acceptFile(file, IMAGE_ACCEPTED_TYPES)) {
    return UIActions.createErrorToasterMessage({
      title: `Image type not supported.`,
    })
  }

  if (file.size > MAX_IMAGE_SIZE) {
    return UIActions.createErrorToasterMessage({
      title: `Only images lower than ${formatFileSize(
        MAX_IMAGE_SIZE
      )} are supported.`,
    })
  }

  return addMediaFileToNote({ uuid, file, meta })
}

export const resetFileUpload = ({ uuid }) => ({
  type: ActionTypes.RESET_FILE_UPLOAD,
  payload: { uuid },
})
